import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { SERVICES } from "@/data/content";

const ServiceRow = ({ service, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const reversed = index % 2 === 1;

  return (
    <div
      ref={ref}
      data-testid={`service-${service.id}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center relative py-16 md:py-24 border-t border-zinc-200"
    >
      {/* Big outline number */}
      <span className="pointer-events-none select-none absolute -top-2 md:-top-6 left-0 font-display font-bold text-[7rem] md:text-[11rem] stroke-number leading-none z-0">
        {service.number}
      </span>

      {/* Text */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`relative z-10 lg:col-span-6 ${reversed ? "lg:order-2 lg:pl-8" : ""}`}
      >
        <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-400">
          {service.label}
        </span>
        <h3 className="font-display font-semibold tracking-tight text-4xl md:text-6xl text-zinc-950 mt-4 leading-none">
          {service.title}
        </h3>
        <p className="mt-6 text-base md:text-lg text-zinc-600 leading-relaxed max-w-md">
          {service.description}
        </p>
        <ul className="mt-8 space-y-3">
          {service.points.map((p) => (
            <li key={p} className="flex items-center gap-3 text-zinc-800">
              <span className="flex-shrink-0 w-6 h-6 rounded-full brand-gradient flex items-center justify-center">
                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              </span>
              <span className="text-sm md:text-base font-medium">{p}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      {/* Image with spotlight + parallax */}
      <div className={`relative z-10 lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-2xl border border-zinc-200 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] aspect-[4/3]"
        >
          <motion.img
            src={service.image}
            alt={service.title}
            style={{ y: imgY }}
            className="absolute inset-0 w-full h-[120%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/30 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 font-mono text-xs tracking-[0.2em] uppercase text-white/90 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
            {service.number} / {service.title}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export const Services = () => {
  return (
    <section
      id="leistungen"
      data-testid="services"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="max-w-3xl mb-4"
      >
        <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-400">
          Meine Leistungen
        </span>
      </motion.div>

      <div>
        {SERVICES.map((service, i) => (
          <ServiceRow key={service.id} service={service} index={i} />
        ))}
      </div>
    </section>
  );
};
